
import PageMainHeading from "@/components/Layout/PageMainHeading.vue";
import ShadowControl from "@/components/Form/ShadowControl.vue";
import BtnContent from "@/components/ui/Buttons/BtnContent.vue";
import ManageTemplateModal from "@/components/Templates/ManageTemplateModal.vue";
import AppButton from "@/components/ui/Buttons/AppButton.vue";

import { mapActions, useStore } from "vuex";
import { templateTableHead } from "@/components/Table/ts/interfaces/tableInstances/templateTable";
import { computed, defineComponent, reactive, watch } from "vue";
import { ITableViewConfiguration } from "@/components/Table/ts/interfaces/common";
import TemplatesTable from "@/components/Table/ts/instances/templates/TemplatesTable.vue";
import { templatesSetup } from "./interfaces/templatesInterfaces";
import {
  IUseBaseAppLayout,
  useBaseAppLayout
} from "@/hooks/layout/useBaseAppLayout";
import { sortOrders } from "@/enums/main/sortOrders";
import AppTableController from "@/components/Table/ts/AppTableController.vue";

export default defineComponent({
  name: "Templates",
  components: {
    AppTableController,
    ManageTemplateModal,
    BtnContent,
    PageMainHeading,
    ShadowControl,
    AppButton,
    TemplatesTable
  },
  setup(): templatesSetup {
    const store = useStore();

    const viewConfiguration: ITableViewConfiguration = reactive({
      page: 1,
      perPage: 10
    });

    // Page layout refs
    const layoutRefs: IUseBaseAppLayout = useBaseAppLayout({
      initialSearchQuery: "",
      onFiltersChange: requestTemplatesHandler,
      onChangeSearchQuery() {
        changeViewConfigurationHandler({
          page: 1,
          perPage: viewConfiguration.perPage
        });
      }
    });

    watch(viewConfiguration, requestTemplatesHandler);

    const { searchQuery } = layoutRefs;

    const fullFilter = computed(() => {
      return {
        search: searchQuery.value,
        sortField: "subject",
        sortType: sortOrders.asc
      };
    });

    async function requestTemplatesHandler(): Promise<void> {
      await store.dispatch("setAppLoading", true);

      const { success, message } = await store.dispatch("fetchTemplateData", {
        query: { ...viewConfiguration, ...fullFilter.value }
      });

      if (!success && message) {
        await store.dispatch("showErrorNotification", message);
      }

      await store.dispatch("setAppLoading", false);
    }

    async function requestTemplateTypes(): Promise<void> {
      await store.dispatch("fetchTemplatesTypes");
    }

    requestTemplatesHandler();
    requestTemplateTypes();

    function changeViewConfigurationHandler({
      page,
      perPage
    }: // search,
    ITableViewConfiguration): void {
      viewConfiguration.page = page;
      viewConfiguration.perPage = perPage;
    }

    return {
      ...layoutRefs,
      viewConfiguration,
      searchQuery,
      templatesList: computed(() => store.getters.templatesList),
      totalTemplatesCount: computed(() => store.getters.totalTemplates),
      requestTemplatesHandler,
      changeViewConfigurationHandler,
      templatesTypesList: computed(() => store.getters.templatesTypesList)
    };
  },

  data() {
    return {
      tableHead: templateTableHead,
      manageTemplateModalOpened: false,
      editingModalData: {
        id: 0,
        code: "",
        status: "",
        subject: "",
        typeId: 0
      },
      editTemplateTitle: "",
      modalLoading: false,
      sortConfig: {
        bySubject: { order: sortOrders.turnOff, keyName: "subject" }
      }
    };
  },

  methods: {
    ...mapActions(["setAppLoading", "showErrorNotification"]),
    toggleManageTemplateModal(data: any) {
      this.manageTemplateModalOpened = data.status;
      if (data.data && data.data.id !== 0) {
        this.editingModalData = data.data;
      }
      if (!data.status) {
        this.editingModalData.id = 0;
      }
    },

    async endEditingTemplate(data: any) {
      if (data) {
        await this.saveTemplateHandler(data);
      }
      await this.requestTemplatesHandler();
      this.toggleManageTemplateModal({ status: false });
      this.editingModalData.id = 0;
    },

    async saveTemplateHandler(data: any) {
      this.setAppLoading(true);
      if (this.editingModalData?.id) {
        await this.$store.dispatch("editTemplateData", {
          data: {
            id: Number(this.editingModalData.id),
            code: data.html,
            subject: data.subject,
            status: data.isActive,
            typeId: data.typeId
          }
        });
      } else {
        await this.$store.dispatch("addTemplate", {
          data: {
            code: data.html,
            subject: data.subject,
            status: data.isActive,
            typeId: data.typeId
          }
        });
      }
      setTimeout(() => {
        this.setAppLoading(false);
      }, 500);
    },

    async deleteTemplate(id: number) {
      await this.$store.dispatch("deleteTemplate", { id });
      await this.requestTemplatesHandler();
      this.toggleManageTemplateModal(false);
    },

    searchHandler({ target }: Event) {
      if (!target) {
        return;
      }

      this.viewConfiguration.search = (target as HTMLInputElement).value;
    }
  }
});
